﻿<script setup lang="ts">
import { onBeforeUnmount, watch } from 'vue'
import AppIcon from './AppIcon.vue'

const props = withDefaults(defineProps<{
  open: boolean
  title: string
  wide?: boolean
  fullScreenMobile?: boolean
  closeDisabled?: boolean
}>(), {
  wide: false,
  fullScreenMobile: false,
  closeDisabled: false
})

const emit = defineEmits<{ close: [] }>()

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape' && props.open && !props.closeDisabled) emit('close')
}

watch(() => props.open, (open) => {
  document.documentElement.classList.toggle('dialog-open', open)
  if (open) window.addEventListener('keydown', onKeydown)
  else window.removeEventListener('keydown', onKeydown)
}, { immediate: true })

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown)
  document.documentElement.classList.remove('dialog-open')
})
</script>

<template>
  <Teleport to="body">
    <Transition name="dialog-fade">
      <div v-if="open" class="dialog-backdrop" role="presentation" @mousedown.self="!closeDisabled && emit('close')">
        <section
          class="dialog-panel"
          :class="{ 'dialog-panel--wide': wide, 'dialog-panel--mobile-full': fullScreenMobile }"
          role="dialog"
          aria-modal="true"
          :aria-label="title"
        >
          <header class="dialog-header">
            <div>
              <p v-if="$slots.eyebrow" class="dialog-eyebrow"><slot name="eyebrow" /></p>
              <h2>{{ title }}</h2>
            </div>
            <button class="icon-button" type="button" aria-label="Close" :disabled="closeDisabled" @click="emit('close')">
              <AppIcon name="close" />
            </button>
          </header>
          <div class="dialog-body"><slot /></div>
          <footer v-if="$slots.footer" class="dialog-footer"><slot name="footer" /></footer>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>
